import React, { useContext, useState } from 'react';

import { Button, Modal } from 'antd';
import {
  BulkAddUsersAttributesContext,
  BulkAddUsersContext,
} from 'common/contexts';
import { fetchWrapper } from 'common/utils/http';
import {
  BULK_ATTRIBUTE_NAME_IND,
  BULK_ATTRIBUTE_VALUE_IND,
} from 'common/constants/constants';
import { validateMemberPhoneNumbers } from './memberValidation';

const makePutAttributesQueryString = (member, bulkAttributes, isDryRun) => {
  let queryString = '';
  Object.keys(member).forEach(attribute => {
    queryString += `${attribute}=${member[attribute]}&`;
  });
  bulkAttributes.forEach(attribute => {
    queryString += `${attribute[BULK_ATTRIBUTE_NAME_IND]}=${attribute[BULK_ATTRIBUTE_VALUE_IND]}&`;
  });

  if (isDryRun) {
    queryString += 'dry_run=true';
  } else {
    queryString = queryString.substring(0, queryString.length - 1);
  }

  return queryString;
};

const makePutAttributesUrl = (newUser, member, bulkAttributes, isDryRun) =>
  `/api/v1/people/${
    newUser.user_id
  }/attributes?${makePutAttributesQueryString(
    member,
    bulkAttributes,
    isDryRun
  )}`;

const attemptCreateNewMembers = async (
  newMembers,
  bulkAttributes,
  setFormError,
  setFormSubmit
) => {
  let formError = null;

  // make sure all bulk added attributes have a value
  bulkAttributes.forEach(attribute => {
    if (!attribute[BULK_ATTRIBUTE_VALUE_IND]) {
      formError = `The "${attribute[BULK_ATTRIBUTE_NAME_IND]}" bulk attribute is missing a value, please add one`;
    }
  });

  // validate member phone numbers
  if (!formError) {
    formError = validateMemberPhoneNumbers(newMembers);
  }

  const truncatedNewMembers = newMembers.slice(0, newMembers.length - 1);

  if (formError) {
    setFormError(formError);
  } else {
    try {
      const testUser = await fetchWrapper('/api/v1/people', 'POST');
      await Promise.all(
        truncatedNewMembers.map(async member => {
          // eslint-disable-next-line no-useless-catch
          try {
            return fetchWrapper(
              makePutAttributesUrl(
                testUser,
                member,
                bulkAttributes,
                true
              ),
              'PUT'
            );
          } catch (e) {
            throw e;
          }
        })
      );

      setFormSubmit(true);
    } catch (e) {
      setFormError(e.responseMessage);
    }
  }
};

const createNewMembers = async (newMembers, bulkAttributes, setFormError) => {
  const putError = null;
  try {
    const truncatedNewMembers = newMembers.slice(0, newMembers.length - 1);
    if (!putError) {
      Promise.all(
        truncatedNewMembers.map(async member => {
          const newUser = await fetchWrapper(
            '/api/v1/people',
            'POST'
          );
          return fetchWrapper(
            makePutAttributesUrl(
              newUser,
              member,
              bulkAttributes,
              false
            ),
            'PUT'
          );
        })
      ).then(() => {
        window.location.href = '/users';
      });
    } else {
      setFormError(putError);
    }
  } catch (e) {
    setFormError(e);
  }
};

const AddMembersButton = () => {
  const [formError, setFormError] = useState(null);
  const [formSubmit, setFormSubmit] = useState(null);
  const { newMembers } = useContext(BulkAddUsersContext);
  const { bulkAttributes } = useContext(BulkAddUsersAttributesContext);
  let addButtonText = null;
  if (newMembers.length > 2) {
    addButtonText = `Add ${newMembers.length - 1} Contacts`;
  } else if (newMembers.length > 1) {
    addButtonText = 'Add 1 Contact';
  }
  return (
    <>
      <Button
        disabled={newMembers.length <= 1}
        onClick={() =>
          attemptCreateNewMembers(
            newMembers,
            bulkAttributes,
            setFormError,
            setFormSubmit
          )}
        type="primary"
      >
        {addButtonText || 'Add'}
      </Button>
      <Modal
        closable={false}
        footer={[
          <Button
            key="ok"
            onClick={() => setFormError(null)}
            type="primary"
          >
            Ok
          </Button>,
        ]}
        onOk={() => setFormError(null)}
        open={!!formError}
        title="Error Creating Members"
      >
        {formError}
      </Modal>
      <Modal
        closable={false}
        onCancel={() => setFormSubmit(null)}
        onOk={() =>
          createNewMembers(
            newMembers,
            bulkAttributes,
            setFormError,
            setFormSubmit
          )}
        open={!!formSubmit}
        title="Confirmation for actions"
      >
        You sure you want to perform these actions?
      </Modal>
    </>
  );
};

export default AddMembersButton;
